import { Controller } from "@hotwired/stimulus";

// Per-distribution-list management page (/forms/:id/distribution_lists/:list_id).
// Calls the existing API endpoints (create_distribution, send_emails,
// send_reminders, send_individual, upload_excel, download_template) and
// reloads the page on success so server-rendered KPIs stay accurate.
export default class extends Controller {
  static targets = [
    "newEmail", "newFirstname", "newLastname",
    "excelInput", "message",
    "templateSelect"
  ];

  static values = {
    listId:                   String,
    createDistributionUrl:    String,
    sendEmailsUrl:            String,
    sendRemindersUrl:         String,
    sendIndividualUrl:        String,
    uploadUrl:                String,
    templateUrl:              String,
    listUpdateUrl:            String,
    csrfToken:                String
  };

  // ── Add a recipient manually ────────────────────────────────────────

  async addRecipient(event) {
    event?.preventDefault?.();
    const email = (this.newEmailTarget?.value || "").trim();
    const firstname = (this.newFirstnameTarget?.value || "").trim();
    const lastname = (this.newLastnameTarget?.value || "").trim();

    if (!email || !email.includes("@")) {
      this._showMessage("Email invalide.", "error");
      this.newEmailTarget?.focus();
      return;
    }

    const recipientName = [firstname, lastname].filter((s) => s.length).join(" ").trim();

    this._showMessage("Ajout en cours…", "info");
    try {
      const res = await fetch(this.createDistributionUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({
          distribution: { recipient_email: email, recipient_name: recipientName }
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err.errors || ["Ajout impossible"]).join(", "));
      }
      window.location.reload();
    } catch (e) {
      this._showMessage(e.message, "error");
    }
  }

  // ── Send emails (bulk pending) ──────────────────────────────────────

  async sendAllPending(event) {
    event?.preventDefault?.();
    if (!confirm("Envoyer le formulaire à tous les destinataires en attente ?")) return;

    this._showMessage("Envoi en cours… cela peut prendre quelques secondes.", "info");
    try {
      const res = await fetch(this.sendEmailsUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({})
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Envoi impossible.");
      window.location.reload();
    } catch (e) {
      this._showMessage(e.message, "error");
    }
  }

  async sendReminders(event) {
    event?.preventDefault?.();
    if (!confirm("Envoyer une relance à tous les destinataires sans réponse ?")) return;

    this._showMessage("Envoi des relances…", "info");
    try {
      const res = await fetch(this.sendRemindersUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({})
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Envoi des relances impossible.");
      window.location.reload();
    } catch (e) {
      this._showMessage(e.message, "error");
    }
  }

  async sendIndividual(event) {
    event?.preventDefault?.();
    const distributionId = event.currentTarget.dataset.distributionId;
    if (!distributionId) return;

    this._showMessage("Envoi en cours…", "info");
    try {
      const res = await fetch(this.sendIndividualUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ distribution_id: distributionId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Envoi impossible.");
      window.location.reload();
    } catch (e) {
      this._showMessage(e.message, "error");
    }
  }

  // ── List default email template ─────────────────────────────────────

  async updateTemplate(event) {
    const templateId = (event?.target?.value || "").trim();
    this._showMessage(templateId ? "Mise à jour du template…" : "Suppression du template par défaut…", "info");

    try {
      const res = await fetch(this.listUpdateUrlValue, {
        method: "PATCH",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({
          jestionform_distribution_list: {
            jestionform_email_template_id: templateId.length ? templateId : null
          }
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data.errors || [data.error || "Mise à jour impossible"]).join(", "));
      this._showMessage("Template mis à jour. Les futurs envois utiliseront ce template.", "success");
      // Reload after a short delay so the "Voir le rendu" button reflects
      // the new template selection.
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      this._showMessage(e.message, "error");
    }
  }

  // ── Excel template + upload ─────────────────────────────────────────

  downloadTemplate(event) {
    event?.preventDefault?.();
    window.location.href = this.templateUrlValue;
  }

  async uploadExcel(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    this._showMessage(`Lecture de « ${file.name} »…`, "info");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(this.uploadUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfTokenValue || (document.querySelector('meta[name="csrf-token"]')?.content || "")
        },
        credentials: "same-origin",
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Import impossible.");

      let msg = `${data.distributions_created || 0} destinataire${(data.distributions_created || 0) > 1 ? "s" : ""} importé${(data.distributions_created || 0) > 1 ? "s" : ""}.`;
      if (Array.isArray(data.errors) && data.errors.length) {
        msg += ` ${data.errors.length} erreur${data.errors.length > 1 ? "s" : ""} : ${data.errors.slice(0, 2).join(" / ")}`;
      }
      this._showMessage(msg, "success");
      // Reset the input so the same file can be re-uploaded
      event.target.value = "";
      // Reload after a short delay so the user can read the message
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      this._showMessage(e.message, "error");
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  _jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": this.csrfTokenValue || (document.querySelector('meta[name="csrf-token"]')?.content || "")
    };
  }

  _showMessage(text, tone) {
    if (!this.hasMessageTarget) return;
    const palette = {
      info:    ["var(--ds-color-info-500, #3b82f6)",    "var(--ds-color-info-50, #eff6ff)",   "var(--ds-color-info-700, #1d4ed8)"],
      success: ["var(--ds-color-success-500)",          "var(--ds-color-success-50, #ecfdf5)","var(--ds-color-success-700)"],
      error:   ["var(--ds-color-danger-600)",           "rgba(220,38,38,0.08)",               "var(--ds-color-danger-700, #991b1b)"]
    };
    const [accent, bg, fg] = palette[tone] || palette.info;
    this.messageTarget.style.cssText = `
      display: flex; gap: 8px; align-items: center;
      padding: 8px 12px;
      border-radius: var(--ds-radius-md);
      background: ${bg}; color: ${fg};
      border-left: 3px solid ${accent};
      font-size: var(--ds-font-size-sm);
    `;
    this.messageTarget.textContent = text;
    this.messageTarget.hidden = false;
  }
}
